import { Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import M3Button from "./M3Button";
import styles from "./m3.module.css";

/**
 * Material 3 standard modal dialog.
 * Features 28px rounded corners, surfaceContainerHigh tonal background, and Poppins typography.
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {React.ReactNode} title
 * @param {string} subtitle
 * @param {React.ReactNode} icon - header icon
 * @param {"standard"|"danger"|"warning"|"success"} variant - icon tonal tint
 * @param {React.ReactNode} children - dialog body content
 * @param {React.ReactNode} actions - action buttons aligned right
 * @param {string} maxWidth - "xs" | "sm" | "md" | "lg"
 */
export const M3Dialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  variant = "standard",
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
}) => {
  const iconClass =
    variant === "danger"
      ? styles.dialogIconDanger
      : variant === "warning"
      ? styles.dialogIconWarning
      : variant === "success"
      ? styles.dialogIconSuccess
      : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      slotProps={{ paper: { className: styles.dialogPaper } }}
      PaperProps={{ className: styles.dialogPaper }}
    >
      {(icon || title || subtitle) && (
        <div className={styles.dialogHeader}>
          {icon && <div className={`${styles.dialogIconContainer} ${iconClass}`}>{icon}</div>}
          <div className={styles.dialogTitles}>
            {title && <h2 className={styles.dialogTitle}>{title}</h2>}
            {subtitle && <p className={styles.dialogSubtitle}>{subtitle}</p>}
          </div>
        </div>
      )}
      {children && <div className={styles.dialogBody}>{children}</div>}
      {actions && <div className={styles.dialogActions}>{actions}</div>}
    </Dialog>
  );
};

/**
 * High-frequency confirmation modal (e.g. Delete, Discard, Confirm action).
 */
export const M3ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm action",
  description,
  confirmLabel,
  cancelLabel,
  danger = false,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <M3Dialog
      open={open}
      onClose={onClose}
      variant={danger ? "danger" : "warning"}
      icon={danger ? <DeleteOutlineOutlinedIcon sx={{ fontSize: 22 }} /> : <WarningAmberOutlinedIcon sx={{ fontSize: 22 }} />}
      title={t(title)}
      actions={
        <>
          <M3Button variant="text" size="md" onClick={onClose} disabled={loading}>
            {cancelLabel ? t(cancelLabel) : t("Cancel")}
          </M3Button>
          <M3Button
            variant={danger ? "dangerFilled" : "filled"}
            size="md"
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel ? t(confirmLabel) : danger ? t("Delete") : t("Confirm")}
          </M3Button>
        </>
      }
    >
      {description && <p style={{ margin: 0 }}>{t(description)}</p>}
    </M3Dialog>
  );
};

export default M3Dialog;
